import { useMemo } from 'react';
import { atom, useAtomValue } from 'jotai';
import { createResourceAtom } from 'lib/atoms';
import { createMutationAtom } from 'atoms/create-resource-atom';
import searchAtomPair from 'atoms/search-atom';
import { formatResource } from 'utils/misc';
import type {
  GeologicIntervalCreateResponse,
  GeologicIntervalListQueryResponse,
  GeologicIntervalValidationResponse,
  OperatorSearchResult,
} from 'types/api-responses';
import type { GeologicIntervalRow } from './types';

export interface BasinIntervalsSearchParams {
  paginated: false;
  basins?: string[];
}

// Per-mount memoized atoms below — fresh AbortController per mount so
// strict-mode double-invoke doesn't reuse a torn-down controller.

export const useBasinIntervalsSearchAtom = () =>
  useMemo(
    () =>
      searchAtomPair<
        GeologicIntervalListQueryResponse,
        BasinIntervalsSearchParams
      >({ href: '/queries/geologic_intervals' }),
    []
  );

export interface CanonicalDefault {
  geologicInterval: string;
  positionalIndex: number;
  age: number | null;
  lithology: string | null;
  displayColor: string | null;
}

// GET /geologic_intervals/canonical_defaults — bare array, no envelope.
export type CanonicalDefaultsResponse = CanonicalDefault[];

export const useCanonicalDefaultsAtom = () =>
  useMemo(
    () =>
      createResourceAtom<CanonicalDefaultsResponse>(
        '/geologic_intervals/canonical_defaults'
      ),
    []
  );

// GET /geologic_intervals/lithology_options — bare array of the server-side
// `Lithology::OPTIONS` whitelist. The list-page facets filter uses
// `facets.lithologies` (DB-distinct) instead, which would shrink the picker.
export type LithologyOptionsResponse = string[];

export const useLithologyOptionsAtom = () =>
  useMemo(
    () =>
      createResourceAtom<LithologyOptionsResponse>(
        '/geologic_intervals/lithology_options'
      ),
    []
  );

// ComboBox-ready `string[]` view of the lithology resource. Used by Add + Edit.
export const useLithologyItems = (): string[] => {
  const lithologyOptionsAtom = useLithologyOptionsAtom();
  const resource = useAtomValue(lithologyOptionsAtom);
  const { data } = formatResource(resource);
  return useMemo(() => (Array.isArray(data) ? data : []), [data]);
};

export type GeologicIntervalDrawerMode = 'add' | 'edit';

export interface GeologicIntervalDrawerState {
  mode: GeologicIntervalDrawerMode | null;
  target: GeologicIntervalRow | null;
}

const DEFAULT_STATE: GeologicIntervalDrawerState = {
  mode: null,
  target: null,
};

export const geologicIntervalDrawerStateAtom =
  atom<GeologicIntervalDrawerState>(DEFAULT_STATE);

export const openAddGeologicIntervalAtom = atom(null, (_get, set) => {
  set(geologicIntervalDrawerStateAtom, { mode: 'add', target: null });
});

export const openEditGeologicIntervalAtom = atom(
  null,
  (_get, set, row: GeologicIntervalRow) => {
    set(geologicIntervalDrawerStateAtom, { mode: 'edit', target: row });
  }
);

export const closeGeologicIntervalDrawerAtom = atom(null, (_get, set) => {
  set(geologicIntervalDrawerStateAtom, DEFAULT_STATE);
});

// POST /geologic_intervals. `seedCanonicalDefaults: true` in the payload
// also seeds the new basin's canonical interval set in the same transaction.
export const createGeologicIntervalAtom = createMutationAtom<
  GeologicIntervalCreateResponse,
  'createGeologicInterval'
>();

export const updateGeologicIntervalAtom = createMutationAtom<
  GeologicIntervalCreateResponse,
  'updateGeologicInterval'
>();

// Same PATCH endpoint as `updateGeologicIntervalAtom`, separate atom so the
// inline ONC add/delete spinner doesn't collide with the footer Save spinner.
export const updateGeologicIntervalOncsAtom = createMutationAtom<
  GeologicIntervalCreateResponse,
  'updateGeologicInterval'
>();

// POST /validations/geologic_intervals — dry-run validator. Always 201 with
// `{ errors, warnings }`; the FE consumes both depending on context.
export const validateGeologicIntervalAtom = createMutationAtom<
  GeologicIntervalValidationResponse,
  'validateGeologicInterval'
>();

export const useOperatorsSearchAtom = () =>
  useMemo(
    () => searchAtomPair<OperatorSearchResult>({ href: '/queries/operators' }),
    []
  );
